import { useState } from "react"

import { useTranslation } from "react-i18next"

import { AlertTriangle, Database, FileBox, HardDrive, Search } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"

export type LibraryToDelete = {
  id: string
  display_name: string
  document_count?: number
  index_name?: string
  output_container?: string
  output_table?: string
}

export type DeleteOptions = {
  deleteIndex: boolean
  deleteTable: boolean
  deleteContainer: boolean
}

type DeleteLibraryDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (options: DeleteOptions) => Promise<void>
  isPending: boolean
  library: LibraryToDelete | null
}

export function DeleteLibraryDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  isPending,
  library,
}: DeleteLibraryDialogProps) {
  const { t } = useTranslation()

  const [deleteIndex, setDeleteIndex] = useState(false)
  const [deleteTable, setDeleteTable] = useState(false)
  const [deleteContainer, setDeleteContainer] = useState(false)

  if (!library) return null

  const handleConfirm = async () => {
    await onConfirm({
      deleteIndex,
      deleteTable,
      deleteContainer,
    })
    // Reset state after confirm
    setDeleteIndex(false)
    setDeleteTable(false)
    setDeleteContainer(false)
    onOpenChange(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset state when closing
      setDeleteIndex(false)
      setDeleteTable(false)
      setDeleteContainer(false)
    }
    onOpenChange(open)
  }

  const tableName = library.output_table
  const hasAzureResources = library.index_name || tableName || library.output_container

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-destructive size-5" />
            {t("adminDashboard.libraries.deleteDialog.title", "Delete Library")}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                {t(
                  "adminDashboard.libraries.deleteDialog.description",
                  "Are you sure you want to delete this library? This action cannot be undone."
                )}
              </p>

              {/* Library Info */}
              <div className="bg-muted/50 rounded-lg border p-3">
                <p className="font-medium">{library.display_name}</p>
                <p className="text-muted-foreground text-xs">{library.id}</p>
                {library.document_count !== undefined && library.document_count > 0 && (
                  <p className="text-warning-ink mt-2 text-sm">
                    {t(
                      "adminDashboard.libraries.deleteDialog.documentWarning",
                      "This library contains {{count}} document(s).",
                      { count: library.document_count }
                    )}
                  </p>
                )}
              </div>

              {/* Azure Resource Cleanup Options */}
              {hasAzureResources && (
                <div className="space-y-3">
                  <p className="text-foreground text-sm font-medium">
                    {t(
                      "adminDashboard.libraries.deleteDialog.cleanupTitle",
                      "Also remove Azure resources?"
                    )}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t(
                      "adminDashboard.libraries.deleteDialog.cleanupDescription",
                      "By default, only the library configuration is removed. Check the options below to also delete the associated Azure resources."
                    )}
                  </p>

                  <div className="space-y-2 rounded-lg border p-3">
                    {/* Delete Search Index */}
                    {library.index_name && (
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={deleteIndex}
                          disabled={isPending}
                          id="delete-index"
                          onCheckedChange={(checked) => setDeleteIndex(checked === true)}
                        />
                        <div className="flex-1">
                          <Label
                            className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                            htmlFor="delete-index"
                          >
                            <Search className="text-primary size-4" />
                            {t(
                              "adminDashboard.libraries.deleteDialog.deleteIndex",
                              "Delete search index"
                            )}
                          </Label>
                          <p className="text-muted-foreground text-xs">{library.index_name}</p>
                        </div>
                      </div>
                    )}

                    {/* Delete Output Table */}
                    {tableName && (
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={deleteTable}
                          disabled={isPending}
                          id="delete-table"
                          onCheckedChange={(checked) => setDeleteTable(checked === true)}
                        />
                        <div className="flex-1">
                          <Label
                            className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                            htmlFor="delete-table"
                          >
                            <Database className="text-foreground size-4" />
                            {t(
                              "adminDashboard.libraries.deleteDialog.deleteTable",
                              "Delete tracking table"
                            )}
                          </Label>
                          <p className="text-muted-foreground text-xs">{tableName}</p>
                        </div>
                      </div>
                    )}

                    {/* Delete Output Container */}
                    {library.output_container && (
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={deleteContainer}
                          disabled={isPending}
                          id="delete-container"
                          onCheckedChange={(checked) => setDeleteContainer(checked === true)}
                        />
                        <div className="flex-1">
                          <Label
                            className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                            htmlFor="delete-container"
                          >
                            <HardDrive className="text-warning-ink size-4" />
                            {t(
                              "adminDashboard.libraries.deleteDialog.deleteContainer",
                              "Delete output container"
                            )}
                          </Label>
                          <p className="text-muted-foreground text-xs">
                            {library.output_container}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {(deleteIndex || deleteTable || deleteContainer) && (
                    <div className="border-destructive/50 bg-destructive/10 flex items-start gap-2 rounded-lg border p-3">
                      <FileBox className="text-destructive mt-0.5 size-4" />
                      <p className="text-destructive text-xs">
                        {t(
                          "adminDashboard.libraries.deleteDialog.destructiveWarning",
                          "Warning: Deleting Azure resources is permanent and cannot be undone. All data in these resources will be lost."
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t("adminDashboard.libraries.deleteDialog.cancel", "Cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isPending}
            onClick={(e) => {
              e.preventDefault()
              void handleConfirm()
            }}
          >
            {isPending ? (
              <>
                <Spinner />
                {t("adminDashboard.libraries.deleteDialog.deleting", "Deleting...")}
              </>
            ) : (
              t("adminDashboard.libraries.deleteDialog.delete", "Delete Library")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
